import React, { FC, useMemo } from 'react';
import {
  Network,
  useExplorerClusterQuery,
  useExplorerRiskQuery,
  useMeQuery,
} from '@apolloGenerated';
import { CopyHash, Typography } from '@rubin-dev/goblin';
import { Blur, ListItem } from '@shared/ui';
import { useTranslation } from 'react-i18next';
import cls from './styles.module.scss';
import { RiskDot } from '@component/NewRisk/ui/RiskDot/RiskDot';
import { amount, format } from '@aml/amount';
import { TransactionsTooltip } from '@component/Address';
import { BigNumber } from 'bignumber.js';
import { useClusterRisk } from '@component/Address/libs/hooks/useClusterRisk';
import { openAddress } from '@shared/libs';
import { RouterName } from '../../../../router';
import { getRiskColor } from '@helpers/address';
import { BalanceTooltip } from '@shared/ui/AddressBalanceCard/BalanceTooltip';
import { setFormatDate } from '@utils/timestamp';

export type ClusterMainInfoProps = {
  wid: string;
  network: Network;
  hideRisk?: boolean;
  hideCluster?: boolean;
  hideTransactions?: boolean;
  hideBalance?: boolean;
  hideActivity?: boolean;
  hideCategory?: boolean;
  hideRootAddress?: boolean;
};
export const ClusterMainInfo: FC<ClusterMainInfoProps> = ({
  wid,
  network,
  hideRisk,
  hideCluster,
  hideTransactions,
  hideBalance,
  hideActivity,
  hideCategory,
  hideRootAddress,
}) => {
  const { data: user } = useMeQuery({ fetchPolicy: 'network-only' });
  const { t } = useTranslation();
  // RISK
  const { data: walletData } = useExplorerClusterQuery({
    variables: { network, id: wid },
  });
  const { data: riskData } = useExplorerRiskQuery({
    variables: { network, address: wid },
    skip: hideRisk,
  });
  const stats = walletData?.explorerClusterStats?.stats;
  const wallet = walletData?.explorerCluster?.cluster;
  const { risk: clusterRisk, category } = useClusterRisk(Number(wallet?.riskCategory));
  const addressRisk = riskData?.explorerRisk?.risk;
  const risk = Number(clusterRisk || addressRisk?.risk);
  const categoryReportedName = addressRisk?.reported?.name;

  // TRANSACTIONS
  const total = stats?.totalCount || 0;
  const totalSent = stats?.sentCount || 0;
  const totalReceived = stats?.receivedCount || 0;

  // BALANCE
  const formatBalance = (val?: string) =>
    Number(val) > 0 ? amount(BigNumber(val || 0).toString(10), network, false) : 0;
  const balance = formatBalance(stats?.totalAmount);
  const balanceIn = formatBalance(stats?.receivedAmount);
  const balanceOut = formatBalance(stats?.sentAmount);

  const expireDate = useMemo(
    () => user?.me.billing.analyzerSubscriptionExpireDate,
    [user?.me.billing.analyzerSubscriptionExpireDate],
  );

  return (
    <>
      <div className={cls.container}>
        <div className={cls.item}>
          {((!hideRisk && !isNaN(risk)) || !expireDate) && (
            <div className={cls.group}>
              <Typography variant="body-14" color="on-surface-primary-1">
                {t('strings.riskscore')}
              </Typography>
              {expireDate ? (
                <Typography tag="div" variant="head-14" className={cls.risk}>
                  <RiskDot risk={Number(risk)} size={12} />
                  {risk}%
                </Typography>
              ) : (
                <Blur to={RouterName.TariffAnalyzer}>
                  <RiskDot risk={Number(risk)} size={12} />
                  000%
                </Blur>
              )}
            </div>
          )}

          {(!!(!hideCluster && wallet) || !expireDate) && (
            <div className={cls.group}>
              <Typography variant="body-14" color="on-surface-primary-1">
                {t('mention.cluster')}
              </Typography>
              <Typography tag={'div'} variant="body-14" className={cls.risk}>
                {expireDate ? (
                  wallet?.owner || 'Entity'
                ) : (
                  <Blur to={RouterName.TariffAnalyzer}>
                    <Typography variant="head-16" color="error-2">
                      Owner
                    </Typography>
                  </Blur>
                )}
              </Typography>
            </div>
          )}
          {!hideRootAddress && (
            <div className={cls.group}>
              <Typography variant="body-14" color="on-surface-primary-1">
                {t('titles.rootAddress')}
              </Typography>
              <CopyHash
                label={wid.slice(0, 8) + '...'}
                hash={wid}
                variant="body-14"
                onClickHash={() => openAddress(network, wid)}
              />
            </div>
          )}
        </div>
        {(!!(!hideCategory && (category || categoryReportedName)) || !expireDate) && (
          <ListItem
            className={cls.item}
            label={`${t('links.category')}:`}
            value={
              expireDate ? (
                <Typography variant="head-14" style={{ color: getRiskColor(risk) }}>
                  {category || categoryReportedName}
                </Typography>
              ) : (
                <Blur to={RouterName.TariffAnalyzer}>
                  <Typography variant="head-16" color="error-2">
                    Category Name
                  </Typography>
                </Blur>
              )
            }
          />
        )}
        {!!(!hideTransactions && walletData) && (
          <ListItem
            className={cls.item}
            label={
              <>
                {`${t('strings.transactions')}:`}{' '}
                <TransactionsTooltip
                  total={format(total)}
                  totalReceive={format(totalReceived)}
                  totalSent={format(totalSent)}
                  dense
                  network={network}
                />
              </>
            }
            value={format(total)}
          />
        )}
        {!!(!hideBalance && walletData) && (
          <ListItem
            className={cls.item}
            label={
              <>
                {`${t('strings.balance')}:`}{' '}
                <BalanceTooltip
                  totalIn={`${balanceIn} ${network}`}
                  totalOut={`${balanceOut} ${network}`}
                  dense
                  network={network}
                />
              </>
            }
            value={`${balance} ${network}`}
          />
        )}
        {!!(!hideActivity && walletData) && (
          <div className={cls.item}>
            <Typography variant="body-14" color="on-surface-primary-1">
              {t('titles.activity')}
            </Typography>
            <Typography tag={'div'} variant="body-14" color="on-surface-primary-1">
              {setFormatDate(stats?.firstTx)}-{setFormatDate(stats?.lastTx)}
            </Typography>
          </div>
        )}
      </div>
    </>
  );
};
